import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../components/AuthContext';
import { 
  FaPlus, 
  FaMinus, 
  FaSearch, 
  FaTimes, 
  FaEye, 
  FaFilter, 
  FaDownload,
  FaBox,
  FaWarehouse,
  FaTruck,
  FaCalendarAlt,
  FaFileAlt,
  FaSave,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaStickyNote,
  FaEdit,
  FaTrash,
  FaFileExport,
  FaUndo,
  FaBoxes,
  FaLayerGroup,
  FaUser,
  FaCog,
  FaTh,
  FaList,
  FaHashtag,
  FaArrowDown,
  FaChevronUp,
  FaChevronDown,
  FaAngleDoubleLeft,
  FaAngleDoubleRight
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Select from 'react-select';
import StockItemSelectionModal from '../../components/StockItemSelectionModal';

const StockItemsOutward = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{
    stockItemId: null,
    stockItemName: '',
    stockItemDetails: null,
    freshQuantity: 0,
    damagedQuantity: 0,
    availableStock: { freshQuantity: 0, damagedQuantity: 0 }
  }]);

  const [showStockItemsModal, setShowStockItemsModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  
  // Outward history state with enhanced pagination
  const [outwardHistory, setOutwardHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOutward, setSelectedOutward] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    location: null,
    startDate: null,
    endDate: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [viewMode, setViewMode] = useState('list');

  // Refs for focus management
  const freshQuantityRefs = useRef([]);
  const damagedQuantityRefs = useRef([]);
  const filterDateStartRef = useRef(null);
  const filterDateEndRef = useRef(null);

  // Initial data loading
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load outward history when filters or pagination changes
  useEffect(() => {
    fetchOutwardHistory();
  }, [historyPage, historyLimit, historyFilters]);



  // Update available stock when location changes
  useEffect(() => {
    if (selectedLocation) {
      const updateAvailableStock = async () => {
        const updatedItems = await Promise.all(
          items.map(async (item) => {
            if (item.stockItemId) {
              const stock = await fetchStockItemStock(item.stockItemId, selectedLocation.value);
              return { ...item, availableStock: stock };
            }
            return item;
          })
        );
        setItems(updatedItems);
      };
      updateAvailableStock();
    }
  }, [selectedLocation]);

  // Update refs when items change
  useEffect(() => {
    freshQuantityRefs.current = freshQuantityRefs.current.slice(0, items.length);
    damagedQuantityRefs.current = damagedQuantityRefs.current.slice(0, items.length);
  }, [items.length]);



  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const [stockItemsRes, locationsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items?userId=${user.user._id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (stockItemsRes.ok) {
        const stockItemsData = await stockItemsRes.json();
        setStockItems(Array.isArray(stockItemsData) ? stockItemsData : stockItemsData.data || []);
      }

      if (locationsRes.ok) {
        const locationsData = await locationsRes.json();
        setLocations(Array.isArray(locationsData) ? locationsData : locationsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load initial data'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOutwardHistory = async () => {
    setHistoryLoading(true);
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        company: companyId,
        ...(historyFilters.location && { location: historyFilters.location }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate })
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items/outward-history?${queryParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      if (data.success) {
        let historyData = data.data || [];
        const total = data.pagination?.totalItems || 0;
        
        if (!Array.isArray(historyData)) {
          historyData = [];
        }
        
        setOutwardHistory(historyData);
        setTotalHistory(total);
        setTotalPages(data.pagination?.totalPages || 0);
      } else {
        setOutwardHistory([]);
        setTotalHistory(0);
      }
    } catch (error) {
      console.error('Error fetching outward history:', error);
      setOutwardHistory([]);
      setTotalHistory(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({
      location: null,
      startDate: null,
      endDate: null
    });
    setHistoryPage(1);
  };

  const fetchStockItemStock = async (stockItemId, locationId) => {
    try {
      const stockItem = stockItems.find(s => s._id === stockItemId);
      if (!stockItem || !stockItem.stock) {
        return { freshQuantity: 0, damagedQuantity: 0 };
      }

      const locationStock = stockItem.stock.find(s => s.location._id === locationId || s.location === locationId);
      if (locationStock) {
        return {
          freshQuantity: locationStock.freshQuantity || 0,
          damagedQuantity: locationStock.damagedQuantity || 0
        };
      }
      
      return { freshQuantity: 0, damagedQuantity: 0 };
    } catch (error) {
      console.error('Error fetching stock item stock:', error);
      return { freshQuantity: 0, damagedQuantity: 0 };
    }
  };

  const handleStockItemSelect = async (stockItem) => {
    const index = currentItemIndex;
    if (index === null) return;

    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Location',
        text: 'Please select a location first'
      });
      return;
    }

    const stock = await fetchStockItemStock(stockItem._id, selectedLocation.value);
    
    // Check if item already exists
    const existingIndex = items.findIndex(item => item.stockItemId === stockItem._id);
    if (existingIndex !== -1 && existingIndex !== index) {
      Swal.fire({
        icon: 'warning',
        title: 'Item Already Added',
        text: 'This stock item is already in the list'
      });
      return;
    }

    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      stockItemId: stockItem._id,
      stockItemName: stockItem.name,
      stockItemDetails: stockItem,
      availableStock: stock
    };
    setItems(updatedItems);
    setShowStockItemsModal(false);
    setCurrentItemIndex(null);

    setTimeout(() => {
      freshQuantityRefs.current[index]?.focus();
    }, 100);
  };

  const addItem = () => {
    setItems([...items, {
      stockItemId: null,
      stockItemName: '',
      stockItemDetails: null,
      freshQuantity: 0,
      damagedQuantity: 0,
      availableStock: { freshQuantity: 0, damagedQuantity: 0 }
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    const numValue = Math.max(0, parseInt(value) || 0);
    
    // Validate against available stock
    if (field === 'freshQuantity' && numValue > updatedItems[index].availableStock.freshQuantity) {
      Swal.fire({
        icon: 'warning',
        title: 'Insufficient Stock',
        text: `Available fresh quantity: ${updatedItems[index].availableStock.freshQuantity}`
      });
      return;
    }
    
    if (field === 'damagedQuantity' && numValue > updatedItems[index].availableStock.damagedQuantity) {
      Swal.fire({
        icon: 'warning',
        title: 'Insufficient Stock',
        text: `Available damaged quantity: ${updatedItems[index].availableStock.damagedQuantity}`
      });
      return;
    }
    
    updatedItems[index][field] = numValue;
    setItems(updatedItems);
  };

  const handleKeyDown = (e, index, field) => {
    const key = e.key;
    
    if (key === 'Enter' || key === 'Tab') {
      e.preventDefault();
      
      if (field === 'freshQuantity') {
        // Move to damaged quantity of current row
        damagedQuantityRefs.current[index]?.focus();
      } else if (field === 'damagedQuantity') {
        // Move to next row fresh quantity, or add new row if this is the last one
        if (index === items.length - 1) {
          addItem();
          setTimeout(() => {
            freshQuantityRefs.current[index + 1]?.focus();
          }, 0);
        } else {
          freshQuantityRefs.current[index + 1]?.focus();
        }
      }
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a location'
      });
      return;
    }

    const validItems = items.filter(item => 
      item.stockItemId && 
      (item.freshQuantity > 0 || item.damagedQuantity > 0)
    );

    if (validItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Valid Items',
        text: 'Please add at least one item with quantity'
      });
      return;
    }

    setSubmitting(true);
    try {
      const outwardData = {
        location: selectedLocation.value,
        date: selectedDate,
        remarks,
        items: validItems.map(item => ({
          stockItemId: item.stockItemId,
          freshQuantity: item.freshQuantity,
          damagedQuantity: item.damagedQuantity
        })),
        createdBy: user.user._id || user.user.id
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/stock-items/outward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(outwardData)
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Stock items outward entry created successfully'
        });
        
        // Reset form
        setItems([{
          stockItemId: null,
          stockItemName: '',
          stockItemDetails: null,
          freshQuantity: 0,
          damagedQuantity: 0,
          availableStock: { freshQuantity: 0, damagedQuantity: 0 }
        }]);
        setRemarks('');
        
        // Refresh history
        fetchOutwardHistory();
        
        // Switch to history tab
        setActiveTab('history');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to create outward entry'
        });
      }
    } catch (error) {
      console.error('Error creating outward entry:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create outward entry'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (outward) => {
    setSelectedOutward(outward);
    setShowDetailsModal(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      [
        'Entry ID', 'Date', 'Location', 'Stock Item Name', 'Stock Item ID', 'Brand', 'Category', 
        'Unit of Measure', 'Fresh Quantity', 'Damaged Quantity', 'Total Quantity', 
        'Created By', 'Created By Email', 'Remarks'
      ],
      ...outwardHistory.flatMap(entry => 
        entry.items.map(item => [
          entry.outwardId || 'N/A',
          new Date(entry.date).toLocaleDateString(),
          entry.locationName || entry.location?.locationName || entry.location?.name || 'N/A',
          item.stockItemId?.name || 'N/A',
          item.stockItemId?.id || 'N/A',
          item.stockItemId?.brand?.name || 'No Brand',
          item.stockItemId?.category?.name || 'No Category',
          item.stockItemId?.unitOfMeasure || 'N/A',
          item.freshQuantity || 0,
          item.damagedQuantity || 0,
          (item.freshQuantity || 0) + (item.damagedQuantity || 0),
          entry.createdBy.name,
          entry.createdBy?.email || 'N/A',
          entry.remarks || ''
        ])
      )
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-items-outward-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // React Select dark mode styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      fontSize: '14px',
      backgroundColor: darkMode ? '#374151' : base.backgroundColor,
      borderColor: darkMode ? '#4B5563' : base.borderColor,
      color: darkMode ? '#F3F4F6' : base.color,
      '&:hover': {
        borderColor: darkMode ? '#6B7280' : base.borderColor
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : base.backgroundColor,
      border: darkMode ? '1px solid #4B5563' : base.border
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? (darkMode ? '#4B5563' : '#EBF5FF')
        : state.isSelected
          ? (darkMode ? '#1F2937' : '#2563EB')
          : (darkMode ? '#374151' : base.backgroundColor),
      color: darkMode ? '#F3F4F6' : base.color,
      '&:active': {
        backgroundColor: darkMode ? '#1F2937' : base.backgroundColor
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : base.color
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : base.color
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9CA3AF' : base.color
    })
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Stock Items Outward</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Manage stock items outward entries and track inventory movement</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className={`flex space-x-8 ${darkMode ? 'border-b border-gray-700' : ''}`} aria-label="Tabs">
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-red-500 text-red-600'
                  : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaPlus className="mr-2" />
              Create Entry
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-red-500 text-red-600'
                  : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaHistory className="mr-2" />
              History ({totalHistory})
            </button>
          </nav>
        </div>

        {/* Entry Form Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaMapMarkerAlt className="inline mr-1" />
                    Location *
                  </label>
                  <Select
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    options={locations.map(location => ({
                      value: location._id,
                      label: location.name || location.locationName
                    }))}
                    placeholder="Select location"
                    isClearable
                    className="text-sm"
                    styles={selectStyles}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaCalendarAlt className="inline mr-1" />
                    Date *
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                    required
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Stock Item
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaBox className="inline mr-1" />
                        Fresh Qty
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaBoxes className="inline mr-1" />
                        Damaged Qty
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Available Stock
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div
                            className={`flex items-center justify-between cursor-pointer p-2 rounded border border-dashed ${darkMode ? 'border-gray-500 hover:bg-gray-600' : 'border-gray-300 hover:bg-blue-50'}`}
                            onClick={() => {
                              setCurrentItemIndex(index);
                              setShowStockItemsModal(true);
                            }}
                          >
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>
                              {item.stockItemName || 'Click to select stock item'}
                            </span>
                            <FaSearch className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                          </div>
                          {item.stockItemDetails && (
                            <div className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ID: {item.stockItemDetails.id} | 
                              Unit: {item.stockItemDetails.unitOfMeasure} |
                              Brand: {item.stockItemDetails.brand?.name}
                            </div>
                          )}
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            ref={el => freshQuantityRefs.current[index] = el}
                            type="number"
                            value={item.freshQuantity}
                            onChange={(e) => handleItemChange(index, 'freshQuantity', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshQuantity')}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            min="0"
                            max={item.availableStock.freshQuantity}
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            ref={el => damagedQuantityRefs.current[index] = el}
                            type="number"
                            value={item.damagedQuantity}
                            onChange={(e) => handleItemChange(index, 'damagedQuantity', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'damagedQuantity')}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            min="0"
                            max={item.availableStock.damagedQuantity}
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <div className="text-green-600">Fresh: {item.availableStock.freshQuantity}</div>
                            <div className="text-red-600">Damaged: {item.availableStock.damagedQuantity}</div>
                          </div>
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div className="flex space-x-1">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              disabled={items.length === 1}
                            >
                              <FaMinus size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Item Button */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <FaPlus className="mr-2" />
                  Add Item
                </button>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Press F2 to select stock item | Enter to move to next field
                </span>
              </div>

              {/* Remarks */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaStickyNote className="inline mr-1" />
                  Remarks
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                  rows="3"
                  placeholder="Enter any remarks..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      Create Outward Entry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className={`rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* History Header */}
            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Outward History</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <FaDownload className="mr-2" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    <FaFilter className="mr-2" />
                    Filters
                  </button>
                </div>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                    <Select
                      value={locations.find(l => l._id === historyFilters.location) ? {
                        value: historyFilters.location,
                        label: locations.find(l => l._id === historyFilters.location)?.name || locations.find(l => l._id === historyFilters.location)?.locationName
                      } : null}
                      onChange={(option) => handleHistoryFilterChange('location', option?.value || null)}
                      options={locations.map(location => ({
                        value: location._id,
                        label: location.name || location.locationName
                      }))}
                      placeholder="All locations"
                      isClearable
                      className="text-sm"
                      styles={selectStyles}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                    <input
                      ref={filterDateStartRef}
                      type="date"
                      value={historyFilters.startDate || ''}
                      onChange={(e) => handleHistoryFilterChange('startDate', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'border-gray-300'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                    <input
                      ref={filterDateEndRef}
                      type="date"
                      value={historyFilters.endDate || ''}
                      onChange={(e) => handleHistoryFilterChange('endDate', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'border-gray-300'}`}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={resetHistoryFilters}
                      className="flex items-center px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                      <FaUndo className="mr-1" />
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* History Table */}
            <div className="overflow-x-auto">
              {historyLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                  <span className={`ml-2 ${darkMode ? 'text-gray-300' : ''}`}>Loading history...</span>
                </div>
              ) : outwardHistory.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No outward entries found
                </div>
              ) : (
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Entry ID
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Date
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Location
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Items Summary
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Quantity
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Created By
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {outwardHistory.map((entry) => (
                      <tr key={entry._id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {entry.outwardId}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatDate(entry.date)}
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                            {entry.locationName || entry.location?.locationName || entry.location?.name || 'N/A'}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div className="space-y-1">
                            <div>{entry.summary?.totalItems || entry.items?.length || 0} items</div>
                            {entry.summary && (
                              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                Fresh: {entry.summary.totalFreshQuantity} | 
                                Damaged: {entry.summary.totalDamagedQuantity}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">
                            {entry.summary?.totalQuantity || 
                             (entry.items?.reduce((sum, item) => sum + (item.freshQuantity || 0) + (item.damagedQuantity || 0), 0) || 0)}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div>
                            {entry.createdByName || 
                             `${entry.createdBy?.name || 'Unknown'} (${entry.createdBy?.id || 'N/A'})`}
                          </div>
                          {entry.createdBy?.email && (
                            <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{entry.createdBy.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(entry)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                          >
                            <FaEye className="mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`px-6 py-3 flex items-center justify-between border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                    disabled={historyPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setHistoryPage(Math.min(totalPages, historyPage + 1))}
                    disabled={historyPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Showing{' '}
                      <span className="font-medium">{((historyPage - 1) * historyLimit) + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(historyPage * historyLimit, totalHistory)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{totalHistory}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setHistoryPage(1)}
                        disabled={historyPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaAngleDoubleLeft />
                      </button>
                      <button
                        onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                        disabled={historyPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {/* Page numbers */}
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        const pageNumber = Math.max(1, Math.min(totalPages - 4, historyPage - 2)) + index;
                        if (pageNumber <= totalPages) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => setHistoryPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNumber === historyPage
                                  ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                  : darkMode ? 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => setHistoryPage(Math.min(totalPages, historyPage + 1))}
                        disabled={historyPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaChevronRight />
                      </button>
                      <button
                        onClick={() => setHistoryPage(totalPages)}
                        disabled={historyPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                      >
                        <FaAngleDoubleRight />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stock Items Selection Modal */}
        <StockItemSelectionModal
          isOpen={showStockItemsModal}
          onClose={() => { setShowStockItemsModal(false); setCurrentItemIndex(null); }}
          onSelect={handleStockItemSelect}
          darkMode={darkMode}
          userId={user?.user?._id}
          locationId={selectedLocation?.value || null}
        />

        {/* Details Modal */}
        {showDetailsModal && selectedOutward && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg shadow-xl w-11/12 md:w-4/5 lg:w-3/4 max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Outward Entry Details - {selectedOutward.outwardId}
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className={darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Entry Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                    <div className="flex items-center">
                      <FaCalendarAlt className="text-red-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Entry Date</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>{formatDate(selectedOutward.date)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <div className="flex items-center">
                      <FaBox className="text-blue-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedOutward.summary?.totalItems || selectedOutward.items?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                    <div className="flex items-center">
                      <FaBoxes className="text-yellow-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Quantity</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedOutward.summary?.totalQuantity || 
                           selectedOutward.items?.reduce((sum, item) => sum + (item.freshQuantity || 0) + (item.damagedQuantity || 0), 0) || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                    <div className="flex items-center">
                      <FaWarehouse className="text-purple-600 mr-2" />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Location</p>
                        <p className={`font-semibold text-xs ${darkMode ? 'text-gray-100' : ''}`}>
                          {selectedOutward.locationName || selectedOutward.location?.locationName || selectedOutward.location?.name || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entry Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                      <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-800'}`}>
                          {selectedOutward.locationName || selectedOutward.location?.locationName || selectedOutward.location?.name || 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {selectedOutward.summary && (
                      <div>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Quantity Breakdown</label>
                        <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-green-600 font-medium">Fresh: </span>
                              <span className={darkMode ? 'text-gray-200' : ''}>{selectedOutward.summary.totalFreshQuantity}</span>
                            </div>
                            <div>
                              <span className="text-red-600 font-medium">Damaged: </span>
                              <span className={darkMode ? 'text-gray-200' : ''}>{selectedOutward.summary.totalDamagedQuantity}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Created By</label>
                      <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedOutward.createdBy?.name || 'Unknown'}
                        </p>
                        {selectedOutward.createdBy?.email && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedOutward.createdBy.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedOutward.remarks && (
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remarks</label>
                    <div className={`p-3 rounded-md ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedOutward.remarks}</p>
                    </div>
                  </div>
                )}

                {/* Items Table */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Outward Items Details</label>
                  <div className={`overflow-x-auto border rounded-lg ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Stock Item Details
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Brand & Category
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Fresh Qty
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Damaged Qty
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total Qty
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Unit
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                        {selectedOutward.items?.map((item, index) => (
                          <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-4">
                              <div>
                                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {item.stockItemId?.name || 'N/A'}
                                </p>
                                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  ID: {item.stockItemId?.id || 'N/A'}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-1">
                                <div className="text-xs">
                                  <span className={`px-2 py-1 rounded ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                                    {item.stockItemId?.brand?.name || 'No Brand'}
                                  </span>
                                </div>
                                <div className="text-xs">
                                  <span className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                                    {item.stockItemId?.category?.name || 'No Category'}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {item.freshQuantity || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {item.damagedQuantity || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {(item.freshQuantity || 0) + (item.damagedQuantity || 0)}
                              </span>
                            </td>
                            <td className={`px-4 py-4 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.stockItemId?.unitOfMeasure || 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Items Summary Footer */}
                  {selectedOutward.summary && (
                    <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items</p>
                          <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOutward.summary.totalItems}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Quantity</p>
                          <p className="text-lg font-semibold text-green-600">{selectedOutward.summary.totalFreshQuantity}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Damaged Quantity</p>
                          <p className="text-lg font-semibold text-red-600">{selectedOutward.summary.totalDamagedQuantity}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Quantity</p>
                          <p className="text-lg font-semibold text-blue-600">{selectedOutward.summary.totalQuantity}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockItemsOutward;
